/**
 * 작성자 : 박승호
 * 날짜 : 2024.08.13
 * 경로 : 카메라 폰트 위치 컴포넌트
 */

import React, { Dispatch, SetStateAction } from 'react';
import styled from 'styled-components';
import icoNot from '../assets/images/icons/ico-not-used.svg';
import icoTl from '../assets/images/icons/ico-position-tl.svg';
import icoTr from '../assets/images/icons/ico-position-tr.svg';
import icoBl from '../assets/images/icons/ico-position-bl.svg';
import icoBr from '../assets/images/icons/ico-position-br.svg';
import { useTranslation } from 'react-i18next';

const Root = styled.div`
  .color-pallete {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
  }

  .button {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.25rem;
    cursor: pointer;
    flex-shrink: 0;

    &.selected {
      .btn-box {
        color: var(--text-reverse) !important;
        background-color: var(--primary) !important;
        &:active {
          color: var(--text-reverse) !important;
          background-color: var(--filled-blue-deep) !important;
        }
      }

      .btn-text {
        font-size: 0.875rem;
        font-weight: bold;
        color: var(--primary) !important;
      }
    }
    &.unselected:hover {
      .btn-box {
        background-color: var(--tonal-deep) !important;
      }
    }

    .btn-box {
      border-radius: 0.25rem;
      padding: 0.45rem 0.725rem;
      background-color: var(--tonal);
      line-height: 0.725rem;

      &.small {
        height: 2rem;
        padding: 0.5rem;
      }
      &.medium {
        height: 2.25rem;
        padding: 0.75rem;
      }
      &.minWidth {
        min-width: 2.25rem;
      }
      &.width40 {
        min-width: 4rem;
      }
      &.width60 {
        min-width: 6rem;
      }
      &.width80 {
        min-width: 8rem;
      }
      .material-symbols-rounded {
        font-size: 1.375rem;
      }

      &.filledIcon {
        .material-symbols-rounded {
          font-variation-settings: 'FILL' 1;
        }
      }

      .name {
        letter-spacing: normal;
        font-size: 0.875rem;
        width: fit-content;
        word-break: keep-all;
        white-space: nowrap;
      }
      &.play_arrow_reverse {
        .material-symbols-rounded {
          transform: rotate(180deg);
        }
      }
      &.negative {
        color: var(--filled-red);
        background-color: var(--tonal-red);
        .name {
          font-weight: 600;
        }
        &:hover {
          background-color: var(--tonal-deep-red);
        }
        &:active {
          color: var(--filled-red-deep);
        }
      }
      &.darkModeOnly {
        color: var(--zinc-200);
        background-color: var(--zinc-800);
        .name {
          font-weight: 400;
        }
        &.selected,
        &:hover {
          color: var(--blue-100);
          background-color: var(--blue-700);
          &:active {
            color: var(--blue-100);
            background-color: var(--blue-600);
          }
        }
        &.unselected:hover {
          color: var(--zinc-100);
          background-color: var(--zinc-600);
        }
        &.negative {
          color: var(--red-500);
          &:hover {
            color: var(--red-100);
            background-color: var(--red-700);
          }
          &:active {
            color: var(--red-100);
            background-color: var(--red-600);
          }
        }
      }
      &.iconWeight_300 {
        .material-symbols-rounded {
          font-variation-settings: 'FILL' 1, 'wght' 300;
        }
      }
    }

    .btn-text {
      font-size: 0.875rem;
      color: var(--zinc-400);
    }
  }
`;

interface ICameraFontPosition {
  camCLocation: string;
  setState: Dispatch<SetStateAction<any>>;
  className?: string;
  minWidth?: boolean;
  darkModeOnly?: boolean;
  iconWeight?: 400 | 300;
  filledIcon?: boolean;
  width?: 'width40' | 'width60' | 'width80';
}

const CameraFontPosition = ({ camCLocation = '0', setState, className, minWidth, darkModeOnly, iconWeight, filledIcon, width }: ICameraFontPosition) => {
  const { t } = useTranslation();
  // 폰트 위치 Options 생성
  const fontLocationOptions = [
    { key: '0', name: '미사용', icon: icoNot },
    { key: '1', name: '왼쪽 상단', icon: icoTl },
    { key: '2', name: '왼쪽 하단', icon: icoBl },
    { key: '3', name: '오른쪽 상단', icon: icoTr },
    { key: '4', name: '오른쪽 하단', icon: icoBr },
  ];

  const setClassName = className ?? '';
  const setMinWidth = minWidth ? 'minWidth' : '';
  const setDarkModeOnly = darkModeOnly ? 'darkModeOnly' : '';
  const setIconWeight = iconWeight ? `iconWeight_${iconWeight}` : '';
  const setFilledIcon = filledIcon ? 'filledIcon' : '';
  const setWidth = width ?? '';

  return (
    <Root>
      <div className='color-pallete'>
        {fontLocationOptions?.map((item) => {
          const defaultSelect = camCLocation || '1';
          const isSelected: boolean = defaultSelect === item.key;
          return (
            <div
              key={item.key}
              role='presentation'
              className={`button ${isSelected ? 'selected' : 'unselected'}`}
              onClick={() => setState && setState((prev: any) => ({ ...prev, camCLocation: item.key }))}
            >
              <div className={`btn-box ${setClassName} ${setMinWidth} ${setDarkModeOnly} ${setIconWeight} ${setFilledIcon} ${setWidth}`}>
                {item.icon && (
                  <span className='material-symbols-rounded'>
                    <img src={item.icon} alt={item.name} />
                  </span>
                )}
              </div>
              {item.name && <span className='btn-text'>{t(item.name)}</span>}
            </div>
          );
        })}
      </div>
    </Root>
  );
};

export default React.memo(CameraFontPosition);
